import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneDeep, filter, flatMap, isEqual } from 'lodash';
import { ColumnShape } from 'react-base-table';
import classnames from 'classnames';

import { useCreatePermissions } from '@hooks/usePermissions';
import { useDeleteMappedEvent, useGetAllMappedEventsInManagement } from '@api/emp/queryHooks';
import { IMappedEventList } from '@src/emp-3.0/types';
import { generateSelectOptions } from '@src/emp-3.0/utils/buildOptions';
import { statusOptions } from '@src/emp-3.0/utils/constants';
import { EPanelOpenMethod } from '@components/block-event-single/store';
import { MappedDetailPanel } from '@components/detail-panel';
import {
  dateFields,
  handleAction,
  MAPPED_EVENTS,
  supportSearchFields,
} from '@src/emp-3.0/constants/management';
import { getFilteredSortedData } from '@src/emp-3.0/utils/management';
import { useDeploy, useUndeploy } from '@src/emp-3.0/api/queryHooks';
import { useDeleteVersion } from '@components/metrics-builder/api/queryHooks';
import { DeleteVersionType } from '@src/emp-3.0/components/Management/components/BulkAction/typed';
import { isDraftVersion, isMultiVersion, isStoragePredefinedMappedEvent } from '@src/emp-3.0/utils';

import {
  ManagementTable,
  SearchFilterPanel,
  useMappedEventManagementStore,
  generateColumn,
  managementTableStyles,
  BulkAction,
} from '../';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { workbenchModeTrueButNotWorkbenchAsset } from '../../WorkbenchButton/util';

const MappedEvents = () => {
  const hasCreate = useCreatePermissions();
  const { workbenchMode } = useInitialInfoStore();
  const {
    openBulkAction,
    operationMode,
    activeEvent,
    setOpenBulkAction,
    setOperationMode,
    setActiveEvent,

    // Multiple selection operation
    selectedList,
    setSelectedList,
    setRemoveInvalidSelectedList,

    // Search
    searchText,
    setSearchText,

    // Filter conditions
    showFilter,
    status,
    category,
    updateDate,
    deployDate,
    createdBy,
    updatedBy,
    setShowFilter,
    setStatus,
    setCategory,
    setUpdateDate,
    setDeployDate,
    setCreatedBy,
    setUpdatedBy,

    // Sort state
    sort,
    setSort,

    // Reset
    resetFilter,
    resetStore,
  } = useMappedEventManagementStore((state) => state);

  const [dataSource, setDataSource] = useState<any[]>([]);

  const { data: mappedEventsList, isFetching: isLoadingMappedEventsList } =
    useGetAllMappedEventsInManagement();
  const { mutate: deployMappedEvent, isLoading: isLoadingDeploy } = useDeploy();
  const { mutate: undeployMappedEvent, isLoading: isLoadingUndeploy } = useUndeploy();
  const { mutate: deleteMappedEvent, isLoading: isLoadingDelete } = useDeleteMappedEvent();
  const { mutate: deleteVersions } = useDeleteVersion();
  const totalCount = useMemo(() => mappedEventsList?.length ?? 0, [mappedEventsList?.length]);

  const isLoading = useMemo(
    () => isLoadingMappedEventsList || isLoadingDelete || isLoadingUndeploy || isLoadingDeploy,
    [isLoadingDelete, isLoadingMappedEventsList, isLoadingUndeploy, isLoadingDeploy],
  );

  const { categoryOptions, createdByOptions, updatedByOptions } = useMemo(
    () => generateSelectOptions(mappedEventsList),
    [mappedEventsList],
  );

  const filteredSortedData = useMemo(
    () =>
      getFilteredSortedData<IMappedEventList>({
        dataList: mappedEventsList,
        filterParams: {
          status,
          category,
          updateDate,
          deployDate,
          createdBy,
          updatedBy,
        },
        searchParams: {
          searchText,
          supportSearchFields,
          dateFields,
        },
        sortParams: sort,
      }),
    [
      category,
      createdBy,
      deployDate,
      mappedEventsList,
      searchText,
      sort,
      status,
      updateDate,
      updatedBy,
    ],
  );

  const tableData = useMemo(
    () =>
      flatMap<IMappedEventList, any>(filteredSortedData, (element, index) =>
        element.versions.map((version) => {
          const id = `${element.event_id}_${version.version_id}`;
          const is_multi_version = element.versions.length > 1;
          return {
            ...version,
            event_id: element.event_id,
            row_number: index,
            id,
            is_multi_version,
            expandedState: {
              // expanded object key is column dataKey
              related_metrics: true,
              related_flows: true,
              related_event: true,
            },
          };
        }),
      ),
    [filteredSortedData],
  );

  const selectableTableData = useMemo(
    () => dataSource.filter((item) => !isStoragePredefinedMappedEvent(item)),
    [dataSource],
  );

  const findActiveEvent = useMemo(() => {
    return filter(dataSource, (item) => {
      return item.event_id === activeEvent?.event_id && item.version_id === activeEvent?.version_id;
    });
  }, [activeEvent, dataSource]);

  useEffect(() => {
    setDataSource(cloneDeep(tableData));
  }, [tableData]);

  useEffect(() => {
    if (findActiveEvent[0]) {
      if (!isEqual(findActiveEvent[0], activeEvent)) {
        setActiveEvent(findActiveEvent[0]);
      }
    }
  }, [findActiveEvent, activeEvent, setActiveEvent]);

  useEffect(() => {
    setRemoveInvalidSelectedList(selectableTableData);
  }, [setRemoveInvalidSelectedList, selectableTableData]);

  const handleToggleExpanded = ({ rowData }) => {
    dataSource.forEach((d) => {
      if (d.id === rowData?.id) d.expandedState = rowData?.expandedState;
    });
    setDataSource([...dataSource]);
  };

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
    },
    [setSearchText],
  );

  const handleShowFilterChange = useCallback(
    (value: boolean) => {
      if (!value) {
        resetFilter();
      }
      setShowFilter(value);
    },
    [resetFilter, setShowFilter],
  );

  const handleCloseEventDetailPanel = useCallback(() => {
    setActiveEvent(undefined);
  }, [setActiveEvent]);

  const handleNameClick = useCallback(
    ({ rowData }) => {
      console.log('rowData', rowData);
      setActiveEvent(rowData);
    },
    [setActiveEvent],
  );

  const handleEdit = useCallback(
    ({ rowData }) => {
      setActiveEvent(rowData);
      setOperationMode(EPanelOpenMethod.EDIT);
    },
    [setActiveEvent, setOperationMode],
  );

  const handleClone = useCallback(
    ({ rowData }) => {
      setActiveEvent(rowData);
      setOperationMode(EPanelOpenMethod.CLONE);
    },
    [setActiveEvent, setOperationMode],
  );

  const handleDeployDimension = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: deployMappedEvent,
          params: {
            resource: 'mapped_events',
            payload: {
              deploy_all: false,
              ids: [rowData.event_id],
            },
          },
          successMessage: 'Deploy Successfully!',
        });
      }
    },
    [deployMappedEvent],
  );

  const handleUndeployMappedEvent = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: undeployMappedEvent,
          params: {
            resource: 'mapped_events',
            payload: {
              ids: [rowData.event_id],
            },
          },
          successMessage: 'Undeploy Successfully!',
        });
      }
    },
    [undeployMappedEvent],
  );

  const handleDeleteMappedEvent = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        const isSubVersion = isMultiVersion(rowData) && isDraftVersion(rowData);
        if (isSubVersion) {
          // TODO: Need to add an API for deleting drafts.
          handleAction({
            action: deleteVersions,
            params: { type: DeleteVersionType.mapped_events, ids: [rowData.version_id] },
            successMessage: 'Delete Successfully!',
          });
        } else {
          handleAction({
            action: deleteMappedEvent,
            params: rowData?.event_id,
            successMessage: 'Delete Successfully!',
          });
        }
      }
    },
    [deleteMappedEvent],
  );

  const columns = useMemo<ColumnShape<any>[]>(() => {
    const columnDefinitions: any[] = [
      {
        columnType: 'number',
      },
      {
        columnType: 'name',
        type: MAPPED_EVENTS,
        title: 'Mapped Event',
        key: 'mapped-event',
        dataKey: 'event_name',
        sort,
        onSortChange: setSort,
        selectedList: selectedList,
        onClick: handleNameClick,
        disable: (rowData) => workbenchModeTrueButNotWorkbenchAsset(rowData?.is_beta),
      },
      {
        columnType: 'status',
        options: statusOptions,
        value: status,
        onChange: setStatus,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'category',
        dataKey: 'event_category',
        options: categoryOptions,
        value: category,
        onChange: setCategory,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'list',
        title: 'Related Metrics',
        key: 'related metrics',
        dataKey: 'related_metrics',
        onClick: handleToggleExpanded,
      },
      {
        columnType: 'list',
        title: 'Related Flows',
        key: 'related flows',
        dataKey: 'related_flows',
        onClick: handleToggleExpanded,
      },
      {
        columnType: 'list',
        title: 'Mapped From',
        key: 'mapped from',
        dataKey: 'related_event',
        onClick: handleToggleExpanded,
      },
      {
        columnType: 'common',
        title: 'Created By',
        key: 'created by',
        dataKey: 'created_by',
        options: createdByOptions,
        value: createdBy,
        onChange: setCreatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'common',
        title: 'Updated By',
        key: 'updated by',
        dataKey: 'updated_by',
        options: updatedByOptions,
        value: updatedBy,
        onChange: setUpdatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Updated At',
        key: 'updated at',
        dataKey: 'updated_at',
        value: updateDate,
        onChange: setUpdateDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Deployed At',
        key: 'deployed at',
        dataKey: 'deployed_at',
        value: deployDate,
        onChange: setDeployDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
    ];
    if (selectableTableData.length > 0) {
      columnDefinitions.unshift({
        columnType: 'checkbox',
        type: MAPPED_EVENTS,
        dataKey: 'event_name',
        selectedList,
        onChange: ({ selectedList }) => {
          setOpenBulkAction(selectedList.length > 0);
          setSelectedList(selectedList);
        },
        data: selectableTableData,
        disable: (rowData) => workbenchModeTrueButNotWorkbenchAsset(rowData?.is_beta),
      });
    }
    if (hasCreate) {
      columnDefinitions.push({
        columnType: 'actions',
        dataKey: 'event_name',
        type: MAPPED_EVENTS,
        onEdit: handleEdit,
        onClone: handleClone,
        onDeploy: handleDeployDimension,
        onUndeploy: handleUndeployMappedEvent,
        onDelete: handleDeleteMappedEvent,
      });
    }

    return columnDefinitions.map((config) => generateColumn(config));
  }, [
    selectedList,
    selectableTableData,
    sort,
    setSort,
    handleNameClick,
    status,
    setStatus,
    showFilter,
    categoryOptions,
    category,
    setCategory,
    createdByOptions,
    createdBy,
    setCreatedBy,
    updatedByOptions,
    updatedBy,
    setUpdatedBy,
    updateDate,
    setUpdateDate,
    deployDate,
    setDeployDate,
    hasCreate,
    setSelectedList,
    handleEdit,
    handleClone,
    handleDeployDimension,
    handleUndeployMappedEvent,
    handleDeleteMappedEvent,
    workbenchMode,
  ]);

  return (
    <ManagementTable
      loading={isLoading}
      columns={columns}
      data={dataSource}
      rowKey="id"
      estimatedRowHeight={50}
      ignoreFunctionInColumnCompare={false}
      header={
        <SearchFilterPanel
          leftComp={
            <>
              <BulkAction
                resource={MAPPED_EVENTS}
                data={selectedList}
                show={openBulkAction}
                onClose={() => {
                  setOpenBulkAction(false);
                  setSelectedList([]);
                }}
              />
              {!openBulkAction && (
                <div style={{ fontWeight: 400 }}>{`Total ${totalCount} Mapped Events`}</div>
              )}
            </>
          }
          searchValue={searchText}
          onSearch={handleSearch}
          filtersVisible={true}
          showFilters={showFilter}
          onShowFilterChange={handleShowFilterChange}
        />
      }
      detail={
        <MappedDetailPanel
          mappedEvent={activeEvent}
          open={!!activeEvent}
          onClose={handleCloseEventDetailPanel}
          operationMode={operationMode}
          setOperationMode={setOperationMode}
        />
      }
      rowClassName={({ rowData }) => {
        return classnames({
          [managementTableStyles.tableRowActive]:
            (activeEvent && activeEvent.id === rowData.id) ||
            selectedList?.find((item) => item.id === rowData.id),
        });
      }}
    />
  );
};

export default MappedEvents;
